import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { get, del } from '../../utils/fetchAPI';
import { FaEdit, FaTrash, FaPlus, FaUserCheck } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PageTitle from "../../components/page-title/pageTitle";
import { RiShieldUserLine } from "react-icons/ri";
import { AnimatePresence, motion } from "framer-motion";
import RoleForm from "./role-form";
import Loader from "../../components/loader/loder";
import { swalDel } from "../../utils/SwalUti";
import { toast } from "react-toastify";
import CustomPagination from "../../components/pagination/CustomPagination";

const RoleList = () => {
    const navigate = useNavigate();
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    useEffect(() => {
        fetchRoles();
    }, [currentPage, perPage]);

    const fetchRoles = async () => {
        setIsLoading(true);
        try {
            const response = await get('/role/list');
            const rolesList = response?.data?.roles || [];
            setRoles(rolesList);
            setTotalRows(response?.data?.total || 0);
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error('Failed to fetch roles');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = () => {
        setSelectedRole(null);
        setShowModal(true);
    };

    const handleEdit = (role) => {
        setSelectedRole(role);
        setShowModal(true);
    };

    const handleDelete = async (roleId, roleName) => {
        const result = await swalDel(`Are you sure you want to delete "${roleName}"? This action cannot be undone.`);
        
        if (result.isConfirmed) {
            try {
                await del(`/role/${roleId}`);
                toast.success('Role deleted successfully!', {
                    position: "top-center",
                    theme: "light",
                });
                fetchRoles();
            } catch (error) {
                console.error('Error deleting role:', error);
                toast.error('Failed to delete role. Please try again.', {
                    position: "top-center",
                    theme: "light",
                });
            }
        }
    };

    const handleFormSuccess = () => {
        fetchRoles();
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedRole(null);
    };

    const columns = [
        {
            name: '#',
            selector: (row, index) => (currentPage - 1) * perPage + index + 1,
            sortable: true,
            width: '70px',
            center: true,
        },
        {
            name: 'Name',
            selector: row => row.name || 'N/A',
            sortable: true,
            wrap: true,
            minWidth: '200px',
        },
        {
            name: 'Slug',
            selector: row => row.slug || 'N/A',
            sortable: true,
            wrap: true,
            minWidth: '180px',
        },
        {
            name: 'Type',
            selector: row => row.type || 'N/A',
            sortable: true,
            width: '150px',
        },
        {
            name: 'Status',
            selector: row => row.status || 'N/A',
            sortable: true,
            width: '120px',
            cell: (row) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    row.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                }`}>
                    {row.status || 'N/A'}
                </span>
            ),
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="flex gap-2">
                    <button
                        className="p-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                        onClick={() => navigate(`/roles/${row._id}/permissions`)}
                        title="Assign Permissions"
                    >
                        <FaUserCheck />
                    </button>
                    <button
                        className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                        onClick={() => handleEdit(row)}
                        title="Edit"
                    >
                        <FaEdit />
                    </button>
                    <button
                        className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        onClick={() => handleDelete(row._id, row.name)}
                        title="Delete"
                    >
                        <FaTrash />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '160px',
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#50b6b5',
                color: 'white',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                paddingLeft: '8px',
                paddingRight: '8px',
            },
        },
        rows: {
            style: {
                fontSize: '0.875rem',
                minHeight: '56px',
            },
            stripedStyle: {
                backgroundColor: '#f6f6f6',
            },
            highlightOnHoverStyle: {
                backgroundColor: '#e0e0e0',
                cursor: 'pointer',
            },
        },
        cells: {
            style: {
                paddingLeft: '8px',
                paddingRight: '8px',
            },
        },
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-full">
                <Loader />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-5 w-full px-2 sm:px-0">
            {/* Header */}
            <div className="flex justify-between items-center">
                <PageTitle title="Roles" icon={<RiShieldUserLine />} />
                <button
                    onClick={handleCreate}
                    className="flex items-center justify-center gap-2 text-sm px-4 py-2 bg-primary text-white rounded-md hover:bg-teal-700 transition-colors"
                >
                    <FaPlus className="w-4 h-4" />
                    Create New Role
                </button>
            </div>

            {/* Roles Table */}
            {roles.length === 0 ? (
                <div className="bg-white rounded-md p-10 text-center drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)]">
                    <p className="text-gray-500 text-lg">No roles found</p>
                    <p className="text-gray-400 text-sm mt-2 mb-4">Create your first role to get started</p>
                    <button
                        onClick={handleCreate}
                        className="flex items-center justify-center gap-2 text-sm px-4 py-2 bg-primary text-white rounded-md hover:bg-teal-700 transition-colors mx-auto"
                    >
                        <FaPlus className="w-4 h-4" />
                        Create New Role
                    </button>
                </div>
            ) : (
                <div className="bg-white rounded-md drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)] overflow-hidden">
                    <DataTable
                        columns={columns}
                        data={roles}
                        customStyles={customStyles}
                        highlightOnHover
                        striped
                        noDataComponent={
                            <div className="text-center py-10 text-gray-500">
                                No roles found
                            </div>
                        }
                    />
                </div>
            )}

            {/* Pagination */}
            {!isLoading && roles.length > 0 && (
                <CustomPagination
                    currentPage={currentPage}
                    totalRows={totalRows}
                    perPage={perPage}
                    setCurrentPage={setCurrentPage}
                    setPerPage={setPerPage}
                />
            )}

            {/* Create/Edit Modal */}
            <AnimatePresence>
                {showModal && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0 }}
                            className="bg-white p-5 rounded-md shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                        >
                            <RoleForm
                                role={selectedRole}
                                onClose={handleCloseModal}
                                onSuccess={handleFormSuccess}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default RoleList;

